/**
 * AddressForm — reusable form untuk Tambah/Edit Alamat Customer.
 *
 * Dipakai oleh: Profile (Tab Alamat) dan Checkout (Dialog "Tambah Alamat").
 *
 * UX principle (sprint UX cleanup):
 *  - Customer TIDAK pernah melihat istilah teknis:
 *      Jubelio, real-time, area_id, latitude, longitude, coordinate.
 *  - Semua integrasi (Jubelio region, koordinat) berjalan di background.
 *  - Field tetap dikirim ke backend (area_id, coordinate) — hanya UI yang
 *    disederhanakan.
 *
 * Backend payload yang dikirim tidak berubah:
 *   { label, recipient_name, phone, address, province, city, district,
 *     subdistrict, postal_code, area_id, province_id, city_id, district_id,
 *     coordinate, is_default }
 */
import React, { useState } from "react";
import { MapPin, Package, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import JubelioRegionPicker from "@/components/JubelioRegionPicker";
import LocationPicker from "@/components/LocationPicker";

const DEFAULT_FORM = {
  label: "Rumah",
  recipient_name: "",
  phone: "",
  address: "",
  city: "",
  postal_code: "",
  is_default: false,
  coordinate: "",
};

export default function AddressForm({ initial, onSaved }) {
  const [form, setForm] = useState(initial || DEFAULT_FORM);
  const [pinOpen, setPinOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Wilayah wajib lengkap — supaya ongkir terhitung otomatis di checkout.
    // Tidak menyebut "Jubelio" / "area_id" ke customer.
    if (!form.area_id) {
      toast.error(
        "Lengkapi Provinsi, Kota, Kecamatan, dan Kelurahan terlebih dahulu",
      );
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (initial?.id) await api.put(`/addresses/${initial.id}`, payload);
      else await api.post("/addresses", payload);
      toast.success("Alamat tersimpan");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-3 max-h-[70vh] overflow-y-auto pr-1"
      data-testid="address-form"
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Label</Label>
          <Input
            value={form.label}
            onChange={(e) => upd("label", e.target.value)}
            placeholder="Rumah / Kantor"
            data-testid="addr-label"
          />
        </div>
        <div>
          <Label>Nama Penerima</Label>
          <Input
            value={form.recipient_name}
            onChange={(e) => upd("recipient_name", e.target.value)}
            required
            data-testid="addr-recipient"
          />
        </div>
      </div>

      <div>
        <Label>No. WhatsApp</Label>
        <Input
          value={form.phone}
          onChange={(e) => upd("phone", e.target.value)}
          required
          data-testid="addr-phone"
        />
      </div>

      <div>
        <Label>Alamat Lengkap</Label>
        <Textarea
          value={form.address}
          onChange={(e) => upd("address", e.target.value)}
          required
          data-testid="addr-address"
        />
      </div>

      {/* Info card: Ongkos Kirim — informatif saja, tanpa toggle/checkbox */}
      <div
        className="p-3 rounded-xl border brand-border-primary brand-soft-bg space-y-1"
        data-testid="shipping-info-card"
      >
        <div className="text-sm font-bold flex items-center gap-1.5 brand-text-primary">
          <Package size={14} /> Ongkos Kirim
        </div>
        <div className="text-[11px] text-[#7A5440] leading-relaxed">
          Lengkapi wilayah pengiriman di bawah agar sistem dapat menghitung
          ongkos kirim secara otomatis saat checkout.
        </div>
      </div>

      {/* Wilayah pengiriman — selalu pakai picker resmi. Tidak ada toggle. */}
      <JubelioRegionPicker
        value={form}
        onChange={(v) =>
          setForm((f) => ({
            ...f,
            ...v,
            city: v.city || f.city,
            postal_code: v.postal_code || f.postal_code,
          }))
        }
      />

      <Label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={!!form.is_default}
          onChange={(e) => upd("is_default", e.target.checked)}
          data-testid="addr-default"
        />{" "}
        Jadikan alamat utama
      </Label>

      {/* PIN Lokasi — tanpa istilah "Latitude/Longitude/Coordinate" */}
      <div
        className="p-3 rounded-xl border brand-border-primary brand-soft-bg space-y-2"
        data-testid="address-pin-section"
      >
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <div className="text-sm font-bold flex items-center gap-1.5 brand-text-primary">
              <MapPin size={14} /> Pin Lokasi
            </div>
            <div
              className="text-[11px] text-[#7A5440] mt-0.5 leading-relaxed"
              data-testid="addr-pin-status"
            >
              {form.coordinate ? (
                <span className="text-[#2D6A4F] flex items-start gap-1">
                  <CheckCircle2 size={12} className="mt-0.5 shrink-0" />
                  <span>
                    Pin lokasi berhasil dipilih. Lokasi siap digunakan untuk
                    proses pengiriman.
                  </span>
                </span>
              ) : (
                <span>
                  Tambahkan pin lokasi agar kurir lebih mudah menemukan alamat
                  Anda. Pin lokasi juga diperlukan agar layanan kurir instant
                  seperti <b>GrabExpress</b> dan <b>GoSend</b> tersedia.
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPinOpen(true)}
            className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded brand-bg-primary text-white hover:opacity-90 transition shrink-0"
            data-testid="addr-pick-pin-btn"
          >
            {form.coordinate ? "Ubah Pin Lokasi" : "Pilih Pin Lokasi"}
          </button>
        </div>
      </div>

      <Button
        type="submit"
        disabled={saving}
        className="brand-btn-primary rounded-xl"
        data-testid="addr-save-btn"
      >
        {saving ? "Menyimpan..." : "Simpan Alamat"}
      </Button>

      {pinOpen && (
        <Dialog open={pinOpen} onOpenChange={setPinOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Pilih Pin Lokasi Alamat</DialogTitle>
              <DialogDescription className="sr-only">
                Drag pin atau klik area peta untuk menentukan titik tepat.
              </DialogDescription>
            </DialogHeader>
            <LocationPicker
              value={form.coordinate || ""}
              onChange={(v) => upd("coordinate", v)}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPinOpen(false)}
                className="rounded-xl"
                data-testid="addr-pin-cancel"
              >
                Batal
              </Button>
              <Button
                type="button"
                onClick={() => setPinOpen(false)}
                className="brand-btn-primary rounded-xl"
                data-testid="addr-pin-save"
              >
                Simpan Pin
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </form>
  );
}
